import { user } from './Utils';

const API_BASE_URL = process.env.REACT_APP_API_SERVER;

async function fetchAPI(path, options = {}) {
  let queryString = '';
  if (options.params) {
    const queryParts = Object.entries(options.params).map(([key, val]) => {
      if (key === 'discount') {
        return `${key}=${val}`;
      }
      return `${key}=${encodeURIComponent(val)}`;
    });

    queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    delete options.params;
  }

  const url = `${API_BASE_URL}${path}${queryString}`;

  const headers = options.body instanceof FormData ? options.headers : {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers
  });

  if (response.status === 401 && path !== '/auth/login') {
    localStorage.removeItem('auth');
    window.location.href = '/auth/login';
  }

  if (response.status === 422 
    || response.status === 403
    || response.status === 404
    || response.status === 429
    || response.status === 400) {
    return response.json();
  }

  if (!response.ok) {
    throw new Error(`API call failed: ${url}, status: ${response.status}`);
  }

  return response.json();
}

// An error the server itself returned (JSON body with statusCode >= 400).
// Its message is safe to show to the user; other errors (network, 5xx text) are not.
export class ApiError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

// fetchAPI resolves 4xx responses with their JSON body instead of throwing (see
// subtask 14). Callers that must not treat an error body as success use this helper.
export function getApiError(response) {
  if (!response || typeof response !== 'object') {
    return null;
  }
  if (typeof response.statusCode !== 'number' || response.statusCode < 400) {
    return null;
  }
  const message = Array.isArray(response.message)
    ? response.message.join(', ')
    : response.message;
  return new ApiError(
    message || response.error || `Request failed with status ${response.statusCode}`,
    response.statusCode
  );
}

function userAuth(){
  return { 'Authorization': `Bearer ${user().token}`};
}

export const apiService = {
  // public
  async getPartners(params) { return fetchAPI('/partners', { method: 'GET', params, headers: userAuth() });},
  async getPartnersMaps(params) { return fetchAPI('/partners/maps/categories', { method: 'GET', params, headers: userAuth() });},
  async getPartnersMapsCluster(params) { return fetchAPI('/partners/maps/cluster', { method: 'GET', params, headers: userAuth() });},
  async clickPartner(params) { return fetchAPI('/partners/'+params.id+'/click', { method: 'GET', params, headers: userAuth() });},
  async getAffiliateСashback(params) { return fetchAPI('/affiliate-partners', { method: 'GET', params });},
  async getAffiliateСashbackLink(params) { return fetchAPI('/affiliate-partners/link/' + params.id, { method: 'GET', params, headers: userAuth() });},
  async getGiveaways(params) { return fetchAPI('/giveaways', { method: 'GET', params, headers: userAuth() });},
  async getGiveaway(params) { return fetchAPI('/giveaways/'+params.id, { method: 'GET', headers: userAuth() });},
  //async getMemberships() { return fetchAPI('/plans/subscriptions', { method: 'GET' });},
  async getMemberships() { return fetchAPI('/plans/latest', { method: 'GET' });},
  //async getOnePay() { return fetchAPI('/plans/one-time', { method: 'GET', headers: userAuth() });},
  async getOnePay(params) { return fetchAPI('/plans/giveaway/'+params.id, { method: 'GET', headers: userAuth() });},
  async getOnePayCreate(data) { return fetchAPI('/one-payments', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async getFAQs() { return fetchAPI('/pages/faqs', { method: 'GET' });},
  async getTermsAndConditions() { return fetchAPI('/pages/terms-and-conditions', { method: 'GET' });},
  async getTerminationOfAccount() { return fetchAPI('/pages/termination-of-account', { method: 'GET' });},
  async getMembershipTermsConditions() { return fetchAPI('/pages/membership-terms-conditions', { method: 'GET' });},
  async getPrivacyPolicy() { return fetchAPI('/pages/privacy-policy', { method: 'GET' });},
  async getBanners(params) { return fetchAPI('/banners', { method: 'GET', params });},
  async getCategories(params) { return fetchAPI('/partners-categories', { method: 'GET', params });},
  async getTags(params) { return fetchAPI('/partners-tags', { method: 'GET', params });},
  async getStates() { return fetchAPI('/partners-locations', { method: 'GET' });},
  async getTestimonials() { return fetchAPI('/testimonials', { method: 'GET' });},
  async sendEmail(data) { return fetchAPI('/support/send-email', { method: 'POST', body: JSON.stringify(data)});},
  async sendEmailPartner(data) { return fetchAPI('/support/send-email/partner', { method: 'POST', body: JSON.stringify(data)});},
  async getPetrolPrices() { return fetchAPI('/gas-stations/prices', { method: 'GET' });},
  async getPetrolLeaderboards(params) { return fetchAPI('/gas-stations/leaderboards/money-saving', { method: 'GET', params });},

  // auth
  async login(data) { return fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify(data)});},
  async forgotPassword(data) { return fetchAPI('/auth/recover-password', { method: 'POST', body: JSON.stringify(data)});},
  async forgotPasswordConfirm(data) { return fetchAPI('/auth/recover-password-confirm', { method: 'POST', body: JSON.stringify(data)});},
  /**/ async refreshToken(data) { return fetchAPI('/auth/refresh-token', { method: 'POST', body: JSON.stringify(data)});},
  async logout() { return fetchAPI('/auth/logout', { method: 'POST', headers: userAuth()});},
  async registrationValidatePhone(data) { return fetchAPI('/validates/phone', { method: 'POST', body: JSON.stringify(data)});},
  async registration(data) { return fetchAPI('/auth/registration', { method: 'POST', body: JSON.stringify(data)});},  
  async registrationCheckPassword(data) { return fetchAPI('/auth/registration/check-password', { method: 'POST', body: JSON.stringify(data)});},  
  async registrationSetPassword(data) { return fetchAPI('/auth/registration/set-password', { method: 'POST', body: JSON.stringify(data)});},  
  async registrationResendEmail(data) { return fetchAPI('/auth/resend-email', { method: 'POST', body: JSON.stringify(data)});},  
  
  // account
  async getUser() { return fetchAPI('/users/me', { method: 'GET', headers: userAuth()});},
  async getUserSessions(params) { return fetchAPI('/auth/sessions', { method: 'GET', params, headers: userAuth() });},
  async deleteUserSession(params) { return fetchAPI('/auth/sessions/' + params.id, { method: 'DELETE', headers: userAuth() });},
  async deleteUserSessions() { return fetchAPI('/auth/sessions', { method: 'DELETE', headers: userAuth() });},
  async updateUser(data) { return fetchAPI('/users/me', { method: 'PATCH', body: JSON.stringify(data), headers: userAuth() });},
  async updateUserEmail(data) { return fetchAPI('/users/change-email', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async updateUserPhone(data) { return fetchAPI('/users/change-phone', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async updateUserPassword(data) { return fetchAPI('/users/change-password', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async deleteUser(data) { return fetchAPI('/users/delete', { method: 'DELETE', body: JSON.stringify(data), headers: userAuth() });},
  async getTickets(params) { return fetchAPI('/support', { method: 'GET', params, headers: userAuth() });},
  async createTicket(data) { return fetchAPI('/support', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async getTicket(params) { return fetchAPI('/support/'+params.id, { method: 'GET', headers: userAuth() });},
  async getTicketMessages(params) { return fetchAPI('/support/'+params.id+'/messages', { method: 'GET', headers: userAuth() });},
  async createTicketMessages(data) { return fetchAPI('/support/'+data.id+'/message', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async getNotifications(params) { return fetchAPI('/notifications', { method: 'GET', params, headers: userAuth() });},
  async readAllNotifications(params) { return fetchAPI('/notifications', { method: 'PATCH', params, headers: userAuth() });},
  async getNotificationsCount(params) { return fetchAPI('/notifications/count', { method: 'GET', params, headers: userAuth() });},
  async getNotificationCenterOptions() { return fetchAPI('/notifications/options', { method: 'GET', headers: userAuth()});},
  async updateNotificationCenterOptions(data) { return fetchAPI('/notifications/options', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async getTransactionHistory(params) { return fetchAPI('/invoices', { method: 'GET', params, headers: userAuth() });},
  async syncPreviousPurchases() { return fetchAPI('/invoices/sync-previous-purchases', { method: 'POST', headers: userAuth() });},
  async getCashback(params) { return fetchAPI('/cashback', { method: 'GET', params, headers: userAuth() });},
  async getAffiliateTransactions(params) { return fetchAPI('/affiliate-partners/transactions', { method: 'GET', params, headers: userAuth() });},
  async getWithdrawals(params) { return fetchAPI('/affiliate-partners/withdrawals', { method: 'GET', params, headers: userAuth() });},
  async withdraw(data) { return fetchAPI('/affiliate-partners/withdrawals', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async beforeUnSubscription(data) { return fetchAPI('/subscriptions/before-cancel/' + data.subscriptionId, { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async unSubscriptionsAll() { return fetchAPI('/subscriptions/cancel', { method: 'DELETE', headers: userAuth() });},
  async unSubscription(params) { return fetchAPI('/subscriptions/cancel/' + params.id, { method: 'DELETE', params, headers: userAuth() });},
  async unSubscriptionWithReason(data) { return fetchAPI('/subscriptions/cancel/' + data.id, { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async goToCustomerPortal() { return fetchAPI('/users/customer-portal', { method: 'POST', headers: userAuth() });},
  async goToSubscriptionCustomerPortal(params) { return fetchAPI('/subscriptions/customer-portal/'+params.id, { method: 'GET', headers: userAuth() });},
  async getSubscriptions(params) { return fetchAPI('/subscriptions', { method: 'GET', params, headers: userAuth()});},
  async getSubscriptionsActive() { return fetchAPI('/subscriptions/active', { method: 'GET', headers: userAuth()});},
  async getPlansByType(params) { return fetchAPI('/plans/type/' + params.type, { method: 'GET', headers: userAuth()});},
  async previewYearlyUpgrade(data) { return fetchAPI('/subscriptions/upgrade-yearly/preview/' + data.subscriptionId, { method: 'POST', body: JSON.stringify({ price: data.price }), headers: userAuth() });},
  async upgradeYearly(data) { return fetchAPI('/subscriptions/upgrade-yearly/' + data.subscriptionId, { method: 'POST', body: JSON.stringify({ price: data.price }), headers: userAuth() });},
  async getOverdueInvoice() { return fetchAPI('/subscriptions/overdue-invoice', { method: 'GET', headers: userAuth()});},
  async changeStatusSubscription(data) { return fetchAPI('/subscriptions/pause/'+data.id+'', { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async uploadImage(data) { return fetchAPI('/upload', { method: 'POST', body: data, headers: userAuth()});},
  async deleteImage(data) { return fetchAPI('/upload', { method: 'DELETE', body: JSON.stringify(data), headers: userAuth()});},
  async getReferrerStats() { return fetchAPI('/referrals/stats', { method: 'GET', headers: userAuth()});},
  async getReferrerLink() { return fetchAPI('/referrals/link', { method: 'GET', headers: userAuth()});},
  async getReferrerHistory(params) { return fetchAPI('/referrals/history', { method: 'GET', params, headers: userAuth()});},
  async getCoupon(data) { return fetchAPI('/subscriptions/add-discount-to-membership/'+data.id, { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async linkPortal(data) { return fetchAPI('/public/portal/'+data.hash, { method: 'POST', body: JSON.stringify(data), headers: userAuth() });},
  async getPetrolSummary() { return fetchAPI('/gas-stations/summary', { method: 'GET', headers: userAuth()});},
};
